import { useState } from 'react';

const ERROR_MESSAGES = {
  invalid_credentials: 'Invalid username or password',
  user_disabled: 'Account disabled — contact administrator',
  login_failed: 'Login failed — please try again',
  email_taken: 'Email already registered — try signing in',
  invalid_email: 'Invalid email format',
  register_failed: 'Registration failed — please try again',
};

export default function LoginPage({ onLogin, onRegister, error, loading }) {
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (mode === 'login') {
      if (!username || !password) return;
      await onLogin(username, password);
    } else {
      if (!username || !password) return;
      if (password.length < 8) return;
      await onRegister?.({ email: username, password, displayName });
    }
  };

  const displayError = error ? (ERROR_MESSAGES[error] || error) : null;
  const tabStyle = (active) => ({
    flex: 1,
    padding: '10px 0',
    fontSize: 14,
    fontWeight: 600,
    color: active ? '#fff' : 'rgba(255,255,255,0.45)',
    background: 'transparent',
    border: 'none',
    borderBottom: active ? '2px solid #fff' : '2px solid transparent',
    cursor: 'pointer',
  });

  return (
    <div className="relative w-full h-full overflow-hidden bg-black">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/login-bg.jpg')" }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

      <div className="relative z-10 flex flex-col h-full justify-end px-6 pb-12">
        <h1 className="text-white text-[28px] font-bold leading-tight mb-2">
          Universal VI
        </h1>
        <p className="text-white/60 text-[14px] mb-6">
          {mode === 'login' ? 'Sign in with your account' : 'Create a new playground account'}
        </p>

        <div style={{ display: 'flex', marginBottom: 16 }}>
          <button type="button" style={tabStyle(mode === 'login')} onClick={() => setMode('login')}>
            Sign in
          </button>
          <button type="button" style={tabStyle(mode === 'register')} onClick={() => setMode('register')}>
            Register
          </button>
        </div>

        {displayError && (
          <p className="text-red-400 text-sm mb-4">{displayError}</p>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Email"
            autoComplete={mode === 'login' ? 'username' : 'email'}
            autoFocus
            className="w-full px-4 py-3.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/40 text-[15px] outline-none focus:border-white/50 transition-colors"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={mode === 'login' ? 'Password' : 'Password (min 8 chars)'}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            className="w-full px-4 py-3.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/40 text-[15px] outline-none focus:border-white/50 transition-colors"
          />
          {mode === 'register' && (
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Display name (optional)"
              autoComplete="name"
              className="w-full px-4 py-3.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/40 text-[15px] outline-none focus:border-white/50 transition-colors"
            />
          )}
          <button
            type="submit"
            disabled={loading || !username || !password || (mode === 'register' && password.length < 8)}
            className="flex items-center justify-center gap-2.5 w-full py-3.5 rounded-full bg-white text-black font-medium text-[15px] active:scale-[0.98] transition-transform disabled:opacity-50"
          >
            {loading
              ? (mode === 'login' ? 'Signing in…' : 'Creating account…')
              : (mode === 'login' ? 'Sign in' : 'Create account')}
          </button>
        </form>
      </div>
    </div>
  );
}
